export type WorkflowRunStep = {

  key: string;
  nodeId: string;
  title: string;
  status: "running" | "succeeded" | "failed" | "skipped";

  iterLabel: string;
  costPoints: number;
  durationMs: number;
  errorMessage: string;

  text: string;
};

export type WorkflowRunState = {
  status: "idle" | "running" | "succeeded" | "failed";
  steps: WorkflowRunStep[];
  costPoints: number;
  durationMs: number;
  errorMessage: string;
  output: Record<string, unknown> | null;
  runId: string;
};

export const EMPTY_WORKFLOW_RUN: WorkflowRunState = {
  status: "idle",
  steps: [],
  costPoints: 0,
  durationMs: 0,
  errorMessage: "",
  output: null,
  runId: "",
};

export type WorkflowRunEvent = {
  type: string;
  nodeId?: string;
  title?: string;
  iterKey?: string;
  status?: string;
  text?: string;
  costPoints?: number;
  durationMs?: number;
  errorMessage?: string;
  output?: unknown;
  message?: string;
  runId?: string;
};

function stepKey(event: WorkflowRunEvent): string {
  return `${event.nodeId ?? ""}@${event.iterKey ?? ""}`;
}

function iterLabel(iterKey: string): string {
  const index = iterKey.lastIndexOf("#");

  return index === -1 ? "" : iterKey.slice(index);
}

export function reduceWorkflowRun(
  state: WorkflowRunState,
  event: WorkflowRunEvent,
): WorkflowRunState {
  switch (event.type) {
    case "run_started":

      return { ...EMPTY_WORKFLOW_RUN, status: "running", runId: event.runId ?? "" };

    case "node_started": {
      const key = stepKey(event);

      const steps = state.steps.filter((step) => step.key !== key);

      return {
        ...state,
        status: "running",
        steps: [
          ...steps,
          {
            key,
            nodeId: event.nodeId ?? "",

            title: event.title || event.nodeId || "",
            status: "running",
            iterLabel: iterLabel(event.iterKey ?? ""),
            costPoints: 0,
            durationMs: 0,
            errorMessage: "",
            text: "",
          },
        ],
      };
    }

    case "node_delta": {
      const key = stepKey(event);

      return {
        ...state,
        steps: state.steps.map((step) =>
          step.key === key ? { ...step, text: step.text + (event.text ?? "") } : step,
        ),
      };
    }

    case "node_finished": {
      const key = stepKey(event);
      const status =
        event.status === "failed"
          ? "failed"
          : event.status === "skipped"
            ? "skipped"
            : "succeeded";

      return {
        ...state,
        steps: state.steps.map((step) =>
          step.key === key
            ? {
                ...step,
                status,
                costPoints: event.costPoints ?? 0,
                durationMs: event.durationMs ?? 0,
                errorMessage: event.errorMessage ?? "",
              }
            : step,
        ),
      };
    }

    case "run_finished":
      return {
        ...state,
        status: event.status === "failed" ? "failed" : "succeeded",
        costPoints: event.costPoints ?? 0,
        durationMs: event.durationMs ?? 0,
        errorMessage: event.errorMessage ?? "",
        output: (event.output as Record<string, unknown>) ?? null,

        steps: state.steps.map((step) =>
          step.status === "running"
            ? { ...step, status: "failed" as const }
            : step,
        ),
      };

    case "error":
      return {
        ...state,
        status: "failed",

        errorMessage: event.message || "",
        steps: state.steps.map((step) =>
          step.status === "running"
            ? { ...step, status: "failed" as const }
            : step,
        ),
      };

    case "done":
      return { ...state, runId: event.runId || state.runId };

    case "cancelled":
      return {
        ...state,
        status: "failed",

        errorMessage: event.message || "",
        steps: state.steps.map((step) =>
          step.status === "running"
            ? { ...step, status: "failed" as const }
            : step,
        ),
      };

    default:
      return state;
  }
}
